import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { API_URL } from '../config/constants';
import { Button, Spin, Alert, Card, Image, Typography, Row, Col, Space, Tag, Empty, Carousel } from 'antd';
import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  FileImageOutlined, 
  MessageOutlined,
  CalendarOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons';

const { Paragraph, Title, Text } = Typography;

interface FitCritic {
  kritik: string;
  fitImageUrl?: string;
  fitImageUrls?: string[];
}

interface Product {
  _id: string;
  modelName: string;
  status?: string;
  fitCritic?: FitCritic;
}

const FitKritikDetayPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const { hasPermission, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canView = isAdmin || hasPermission('fitcrt', 'read');
  const canEdit = isAdmin || hasPermission('fitcrt', 'update') || hasPermission('fitcrt', 'create');

  useEffect(() => {
    if (productId && canView) {
      fetchProductDetails();
    } else {
      if (!productId && canView) {
        setError("Ürün ID bulunamadı.");
        navigate('/fit');
      } else if (productId && !canView) {
        setError("Bu sayfayı görüntüleme yetkiniz yok.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, canView]);

  const fetchProductDetails = async () => {
    if (!productId) {
      setError("Ürün ID bulunamadı.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/products/${productId}`);
      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Ürün bulunamadı. Lütfen geçerli bir ürün seçtiğinizden emin olun.');
        } else {
          throw new Error('Ürün detayları getirilemedi');
        }
      }
      const data: Product = await res.json();
      setProduct(data);
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Ürün fotoğraflarını kontrol eden yardımcı fonksiyon
  const getFitImages = (): string[] => {
    if (!product || !product.fitCritic) return [];
    
    // Eğer yeni fitImageUrls dizisi varsa onu kullan
    if (product.fitCritic.fitImageUrls && product.fitCritic.fitImageUrls.length > 0) {
      return product.fitCritic.fitImageUrls;
    }
    
    // Eski format - geriye dönük uyumluluk
    if (product.fitCritic.fitImageUrl) {
      return [product.fitCritic.fitImageUrl];
    }
    
    return [];
  };

  const hasKritik = product?.fitCritic?.kritik;
  const fitImages = getFitImages();
  const hasFitImages = fitImages.length > 0;

  // Eğer fit kritiği yapılmamışsa ve canEdit true ise doğrudan edit sayfasına yönlendir
  /*
  if (!hasKritik && canEdit) {
    navigate(`/fit/${productId}/edit`);
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Spin tip="Düzenleme sayfasına yönlendiriliyorsunuz..." />
      </div>
    );
  }
  */

  if (!canView && !loading) {
    return (
      <div className="p-8">
        <Alert 
          message="Yetkisiz Erişim" 
          description="Bu sayfayı görüntüleme yetkiniz yok." 
          type="error" 
          showIcon 
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spin size="large" tip="Ürün bilgileri yükleniyor..." />
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="p-8">
        <Alert 
          message="Veri Yükleme Hatası" 
          description={error} 
          type="error" 
          showIcon 
          className="shadow-sm"
        />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-8">
        <Alert 
          message="Ürün Bulunamadı" 
          description="Belirtilen ürün yüklenemedi veya bulunamadı." 
          type="warning" 
          showIcon 
          className="shadow-sm"
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header with Navigation and Action Buttons */}
      <div className="flex justify-between items-center mb-6">
        <Space>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/fit')}
            className="hover:bg-gray-50"
            size="large"
          >
            Fit Kritik Listesi
          </Button>
        </Space>
        
        {canEdit && hasKritik && (
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => navigate(`/fit/${productId}/edit`)}
            className="shadow-sm hover:shadow"
          >
            Düzenle
          </Button>
        )}
      </div>
      
      {/* Product Header Card */}
      <Card 
        className="mb-6 shadow-sm hover:shadow-md transition-shadow duration-300"
        style={{ borderRadius: '8px' }}
      >
        <div className="flex items-center gap-4">
          <div className="flex-grow">
            <Title level={3} style={{ margin: 0 }}>
              {product.modelName}
            </Title>
            <Space className="mt-2">
              <Tag color="blue">
                <InfoCircleOutlined className="mr-1" />
                {product.status || 'Aktif'}
              </Tag>
              <Tag color={hasKritik ? 'green' : 'red'}>
                <FileTextOutlined className="mr-1" />
                {hasKritik ? 'Fit kritiği yapıldı' : 'Fit kritiği yapılmadı'}
              </Tag>
              {hasFitImages && (
                <Tag color="purple">
                  <FileImageOutlined className="mr-1" />
                  {fitImages.length} Fotoğraf
                </Tag>
              )}
            </Space>
          </div>
        </div>
      </Card>

      {/* Main Content Area */}
      {!hasKritik ? (
        <Card 
          style={{ borderRadius: '8px' }} 
          className="shadow-sm"
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span className="text-gray-500">
                Bu ürün için henüz bir fit kritiği yapılmamış
              </span>
            }
          >
            {canEdit && (
              <Button 
                type="primary" 
                icon={<EditOutlined />} 
                onClick={() => navigate(`/fit/${productId}/edit`)}
              >
                Fit Kritiği Ekle
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        <Row gutter={[24, 24]}>
          {/* Fit Kritik Detayları */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <Space>
                  <MessageOutlined style={{ color: '#1890ff' }} />
                  <span>Fit Kritik Notları</span>
                </Space>
              }
              style={{ borderRadius: '8px', height: '100%' }} 
              className="shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              <div className="bg-gray-50 p-4 rounded-lg min-h-[150px]">
                <Paragraph 
                  style={{ 
                    whiteSpace: 'pre-wrap',
                    fontSize: '15px',
                    lineHeight: '1.6',
                    color: '#333'
                  }}
                >
                  {product.fitCritic?.kritik || "Kritik notu bulunmuyor."}
                </Paragraph>
              </div>
            </Card>
          </Col>
          
          {/* Fit Fotoğrafları */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <Space>
                  <FileImageOutlined style={{ color: '#1890ff' }} />
                  <span>Fit Fotoğrafları</span>
                </Space>
              }
              style={{ borderRadius: '8px', height: '100%' }} 
              className="shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              {hasFitImages ? (
                <div className="fit-images-carousel">
                  {fitImages.length === 1 ? (
                    // Tek fotoğraf varsa carousel kullanmaya gerek yok
                    <div className="flex justify-center p-2">
                      <Image 
                        src={`${API_URL}${fitImages[0]}`}
                        alt="Fit Fotoğrafı" 
                        style={{ 
                          maxWidth: '100%',
                          maxHeight: '400px',
                          objectFit: 'contain',
                          borderRadius: '4px'
                        }}
                      />
                    </div>
                  ) : (
                    // Birden fazla fotoğraf varsa carousel kullan
                    <Carousel
                      arrows
                      prevArrow={<Button icon={<LeftOutlined />} type="text" />}
                      nextArrow={<Button icon={<RightOutlined />} type="text" />}
                      className="p-2"
                    >
                      {fitImages.map((imageUrl, index) => (
                        <div key={index} className="flex justify-center">
                          <Image 
                            src={`${API_URL}${imageUrl}`}
                            alt={`Fit Fotoğrafı ${index + 1}`}
                            style={{ 
                              maxWidth: '100%',
                              maxHeight: '400px',
                              objectFit: 'contain',
                              borderRadius: '4px'
                            }}
                          />
                        </div>
                      ))}
                    </Carousel>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg min-h-[150px]">
                  <FileImageOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                  <Text className="mt-4 text-gray-500">
                    Fit fotoğrafı yüklenmemiş
                  </Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default FitKritikDetayPage; 